import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/routes/ProtectedRoute';
import AdminRoute from '@/routes/AdminRoute';
import ClubesRoute from '@/routes/ClubesRoute';
import AdminOrClubesRoute from '@/routes/AdminOrClubesRoute';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ClubesPage = lazy(() => import('@/pages/ClubesPage'));
const BrevetsPage = lazy(() => import('@/pages/BrevetsPage'));
const HomologadosPage = lazy(() => import('@/pages/HomologadosPage'));
const ImportarHomologacoesPage = lazy(() => import('@/pages/ImportarHomologacoesPage'));
const CiclistasPage = lazy(() => import('@/pages/CiclistasPage'));
const RandonneurPage = lazy(() => import('@/pages/RandonneurPage'));
const UsuariosPage = lazy(() => import('@/pages/UsuariosPage'));
const MeuClubePage = lazy(() => import('@/pages/MeuClubePage'));
const MedalStockPage = lazy(() => import('@/pages/MedalStockPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const OutrosPage = lazy(() => import('@/pages/OutrosPage'));
const MapaClubesPage = lazy(() => import('@/pages/MapaClubesPage'));
const CondecoradosPage = lazy(() => import('@/pages/CondecoradosPage'));
const ImportarCondecoradosPage = lazy(() => import('@/pages/ImportarCondecoradosPage'));
const CalendarioPage = lazy(() => import('@/pages/CalendarioPage'));

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route element={<ClubesRoute />}>
                <Route path="/clube" element={<MeuClubePage />} />
              </Route>
              <Route element={<AdminOrClubesRoute />}>
                <Route path="/brevets" element={<BrevetsPage />} />
                <Route path="/calendario" element={<CalendarioPage />} />
                {/* Tela de segundo nível: alcançada pela ação do card, sem
                  NavItem próprio na Sidebar */}
                <Route path="/brevets/:id/homologados" element={<HomologadosPage />} />
                {/* Clubes entra numa visão escopada ao próprio clube (saldo,
                  envios e ajustes dele); o recorte real é do backend */}
                <Route path="/medalhas-estoque" element={<MedalStockPage />} />
                {/* Clubes leem todos os condecorados; cadastrar, editar, excluir e
                  importar é só de Admin, e quem garante é o backend. */}
                <Route path="/condecorados" element={<CondecoradosPage />} />
              </Route>
              {/* Fora de "Meu Clube", dos brevets do próprio clube e da visão
                escopada do estoque de medalhas, o grupo Clubes não alcança nada
                (docs/business-rules.md § Controle de Acesso por Perfil): estas
                listagens administrativas são Admin-only, como a Sidebar já
                reflete. A autorização real continua no backend. */}
              {/* Importar homologações é Admin-only: fica fora do bloco que o
                grupo Clubes alcança, ao contrário da consulta de homologados. */}
              <Route element={<AdminRoute />}>
                <Route path="/brevets/:id/homologar" element={<ImportarHomologacoesPage />} />
                <Route path="/clubes" element={<ClubesPage />} />
                <Route path="/ciclistas" element={<CiclistasPage />} />
                <Route path="/randonneurs" element={<RandonneurPage />} />
                <Route path="/usuarios" element={<UsuariosPage />} />
                <Route path="/condecorados/importar" element={<ImportarCondecoradosPage />} />
                <Route path="/mapa-clubes" element={<MapaClubesPage />} />
                {/* Hub "Outros", hoje sem atalhos e fora do menu. O guard tem que
                  coincidir com os grupos declarados em ATALHOS_OUTROS: item visível
                  cujo guard recusa vira "acesso negado" no clique. */}
                <Route path="/outros" element={<OutrosPage />} />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
